import { formatAllocation } from '../lib/allocation';
import type { CollectionItemOption } from '../lib/types';
import type { AllocationMap } from '../lib/allocation';

/**
 * Delivery-leg allocation table. Lists the order's collected cargo items and
 * lets the operator draw a quantity from each — so a delivery can only ever
 * deliver what's been collected (commodity + remaining quantity constrained).
 * The delivery's cargo items are derived from these allocations on save.
 */
export function AllocationTable({
  items,
  availableById,
  value,
  onChange,
  disabled,
}: {
  /** All collected items in the order (the pool). */
  items: CollectionItemOption[];
  /** Max this delivery can take per collection item id (collected − delivered elsewhere). */
  availableById: Record<string, number>;
  value: AllocationMap;
  onChange: (next: AllocationMap) => void;
  disabled?: boolean;
}) {
  const set = (id: string, qty: number) => {
    const next = { ...value };
    if (qty > 0) next[id] = qty;
    else delete next[id];
    onChange(next);
  };

  // Show items still available, plus any this delivery already draws from
  // (so existing allocations stay visible/adjustable even when others took the rest).
  const rows = items.filter((c) => (availableById[c.id] ?? 0) > 0 || (value[c.id] ?? 0) > 0);

  // A total only means something when every row is in the same unit.
  const measures = new Set(rows.map((c) => c.measure));
  const singleMeasure = measures.size === 1 ? rows[0]?.measure : undefined;
  const totalQty = Object.values(value).reduce((s, q) => s + q, 0);

  if (items.length === 0) {
    return (
      <p className="rounded-sm border border-dashed border-slate-300 bg-white px-3 py-4 text-center text-[11px] italic text-slate-400">
        Add a collection first — a delivery draws its goods from the order's collections.
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-sm border border-dashed border-slate-300 bg-white px-3 py-4 text-center text-[11px] italic text-slate-400">
        All collected goods are already allocated to other deliveries.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
        Allocate goods to deliver
      </span>
      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="w-full table-auto text-xs">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold">From collection</th>
              <th className="px-2 py-1.5 text-left font-semibold">Item</th>
              <th className="px-2 py-1.5 text-right font-semibold">Collected</th>
              <th className="px-2 py-1.5 text-right font-semibold">Remaining</th>
              <th className="px-2 py-1.5 text-right font-semibold">Deliver</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => {
              const available = availableById[c.id] ?? 0;
              const current = value[c.id] ?? 0;
              const remaining = available - current;
              // The product when the row carries one, else its description.
              const itemLabel = c.productLabel ?? c.description ?? '—';
              return (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1.5 text-slate-600">{c.taskLabel}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{itemLabel}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-500">
                    {formatAllocation(c.collectedQty, c.measure)}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums text-slate-500">
                    {formatAllocation(remaining, c.measure)}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <input
                        type="number"
                        min={0}
                        max={available}
                        value={current === 0 ? '' : String(current)}
                        disabled={disabled}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            set(c.id, 0);
                            return;
                          }
                          const n = Math.floor(Number(raw));
                          if (!Number.isFinite(n) || n < 0) {
                            set(c.id, 0);
                            return;
                          }
                          set(c.id, Math.min(n, available));
                        }}
                        aria-label={c.measure === 'count' ? 'Deliver' : `Deliver (${c.measure === 'kg' ? 'kg' : 'L'})`}
                        className={`h-7 ${c.measure === 'count' ? 'w-16' : 'w-24'} rounded-sm border border-slate-300 bg-white px-1.5 text-right text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400`}
                      />
                      {c.measure !== 'count' && (
                        <span className="text-[10px] text-slate-500">{c.measure === 'kg' ? 'kg' : 'L'}</span>
                      )}
                      <button
                        type="button"
                        disabled={disabled || current >= available}
                        onClick={() => set(c.id, available)}
                        className="rounded-sm px-1.5 py-0.5 text-[10px] font-medium text-purple-600 hover:bg-purple-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                      >
                        All
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td colSpan={4} className="px-2 py-1.5 text-right text-[11px] text-slate-500">
                Delivering
              </td>
              <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs font-semibold tabular-nums text-slate-800">
                {singleMeasure ? formatAllocation(totalQty, singleMeasure) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
